import { getSubjectLabels } from '@/lib/settings';
import { FaelligkeitFeld, ZuweisungFeld } from '../tasks/AufgabenFelder';
import { TAGESENDE, faelligkeitAusWert, faelligkeitText } from '../../lib/faelligkeit';
import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import RichTextEditor from '../ui/rich-text-editor';
import { api, historyTemplatesApi } from '../../lib/api';
import { TrialLessonDTO, cancelTrialAdvanced } from '../../lib/trials';
import { formatDate } from '../../lib/dateFormat';
import { useAuth } from '../auth/use-auth';
import WizardShell from '../ui/wizard-shell';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge, WizardPills, WizardPill } from '../ui/wizard-layout';
import { toast } from 'sonner';

type ReasonKey = 'student_cancelled' | 'teacher_cancelled' | 'no_show' | 'other';

const getReasonLabel = (reasonKey: ReasonKey, reasonText: string): string => {
  switch (reasonKey) {
    case 'other':
      return reasonText || 'anderer Grund';
    case 'student_cancelled':
      return 'Schüler hat abgesagt';
    case 'teacher_cancelled':
      return 'Lehrer hat abgesagt';
    case 'no_show':
    default:
      return 'Schüler ist nicht erschienen';
  }
};

const parseSubprofileRecipient = (rawSubprofileId: string): { wpUserId: number; subIndex: number | null } => {
  const subprofileId = String(rawSubprofileId || '').trim();
  if (!subprofileId) return { wpUserId: 0, subIndex: null };
  if (subprofileId.includes('-')) {
    const [wpIdPart, subIndexPart] = subprofileId.split('-', 2);
    return {
      wpUserId: Number.parseInt(wpIdPart || '0', 10) || 0,
      subIndex: subIndexPart ? (Number.parseInt(subIndexPart, 10) || null) : null,
    };
  }
  return { wpUserId: Number.parseInt(subprofileId, 10) || 0, subIndex: null };
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  onDone?: (updated: TrialLessonDTO) => void;
}

/** Ersatz, falls die Vorlage keine Aufgabenbeschreibung liefert. */
const STANDARD_AUFGABE = 'Ist bereits eine neue PS vereinbart?\nPS machen oder in Historie eintragen, warum das nicht gewünscht ist.';

const CancelTrialWizardModal: React.FC<Props> = ({ open, onOpenChange, trial, onDone }) => {
  const { user } = useAuth();
  const currentUserId = useMemo(() => (user ? Number.parseInt(user.id, 10) : undefined), [user]);

  const [step, setStep] = useState<number>(1);
  const [reasonKey, setReasonKey] = useState<ReasonKey>('student_cancelled');
  const [reasonText, setReasonText] = useState<string>('');
  const [sendEmailTeacher, setSendEmailTeacher] = useState<boolean>(true);
  const [emailSubjectTeacher, setEmailSubjectTeacher] = useState<string>('Probestunde abgesagt');
  const [emailHtmlTeacher, setEmailHtmlTeacher] = useState<string>('');
  const [emailTextTeacher, setEmailTextTeacher] = useState<string>('');
  const [sendEmailStudent, setSendEmailStudent] = useState<boolean>(true);
  const [emailSubjectStudent, setEmailSubjectStudent] = useState<string>('Probestunde abgesagt');
  const [emailHtmlStudent, setEmailHtmlStudent] = useState<string>('');
  const [emailTextStudent, setEmailTextStudent] = useState<string>('');

  const [taskDescription, setTaskDescription] = useState<string>(STANDARD_AUFGABE);
  // Solange niemand die Beschreibung angefasst hat, gewinnt die Vorlage.
  const [taskDescriptionTouched, setTaskDescriptionTouched] = useState<boolean>(false);
  const [assignedUserId, setAssignedUserId] = useState<number | undefined>(currentUserId);
  const [dueAt, setDueAt] = useState<string>('');
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);
  const [historyHtml, setHistoryHtml] = useState<string>('');
  const [historyTouched, setHistoryTouched] = useState<boolean>(false);

  const reasonLabel = useMemo(() => getReasonLabel(reasonKey, reasonText), [reasonKey, reasonText]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSendEmailTeacher(true);
    setSendEmailStudent(true);
    const today = new Date();
    today.setHours(23, 59, 0, 0);
    const iso = new Date(today.getTime() - today.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setDueAt(iso);
    // Default Historie setzen
    const datum = formatDate(trial.date);
    const fach = (trial as any).fachName || '';
    const lehrer = trial.teacherName || '';
    const grund = (() => {
      if (reasonKey === 'other') return reasonText || 'anderer Grund';
      if (reasonKey === 'student_cancelled') return 'Schüler hat abgesagt';
      if (reasonKey === 'teacher_cancelled') return 'Lehrer hat abgesagt';
      return 'Schüler ist nicht erschienen';
    })();
    setHistoryHtml(`Die Probestunde am ${datum} für ${trial.studentName} bei ${lehrer} (${getSubjectLabels().singular}: ${fach}) fand nicht statt. Als Grund wurde angegeben: ${grund}`);
    setHistoryTouched(false);
    setTaskDescription(STANDARD_AUFGABE);
    setTaskDescriptionTouched(false);
  }, [open]);

  useEffect(() => {
    if (!open || historyTouched) return;
    let cancelled = false;
    const loadTemplate = async () => {
      try {
        const rendered = await historyTemplatesApi.render({
          template_key: 'trial.cancelled.history',
          standort_id: standortId || undefined,
          actor_user_id: currentUserId,
          vars: {
            studentName: trial.studentName || '',
            teacherName: trial.teacherName || '',
            fachName: (trial as any).fachName || '',
            date: formatDate(trial.date),
            reason: reasonLabel,
          },
        });
        if (cancelled) return;
        if (rendered.history) setHistoryHtml(rendered.history);
        if (rendered.task) {
          const vorlage = rendered.task.description?.trim();
          if (vorlage && !taskDescriptionTouched) setTaskDescription(vorlage);
          if (rendered.task.dueAt) {
            setDueAt(rendered.task.dueAt.replace(' ', 'T').slice(0, 16));
          }
          if (rendered.task.assignedUserId) {
            setAssignedUserId(rendered.task.assignedUserId);
          }
        }
      } catch {
        const datum = formatDate(trial.date);
        const fach = (trial as any).fachName || '';
        const lehrer = trial.teacherName || '';
        setHistoryHtml(`Die Probestunde am ${datum} für ${trial.studentName} bei ${lehrer} (${getSubjectLabels().singular}: ${fach}) fand nicht statt. Als Grund wurde angegeben: ${reasonLabel}`);
      }
    };
    void loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, trial, reasonLabel, historyTouched, currentUserId]);

  // Benutzerliste für Zuweisung laden
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const list = await api.get<Array<{ id: number; vorname: string; nachname: string }>>('/api/users');
        if (cancelled) return;
        setUsers(list.map(u => ({ id: u.id, name: `${u.vorname} ${u.nachname}`.trim() })));
        if (!assignedUserId && currentUserId) setAssignedUserId(currentUserId);
      } catch {
        if (currentUserId) setUsers([{ id: currentUserId, name: user?.name || `User #${currentUserId}` }]);
      }
    };
    if (open) loadUsers();
    return () => { cancelled = true; };
  }, [open, currentUserId, user, assignedUserId]);

  const canContinueStep1 = reasonKey !== 'other' || reasonText.trim().length > 0;
  const canConfirm = assignedUserId && dueAt !== '';
  const canMoveNext = step === 1 ? canContinueStep1 : true;

  const standortId = useMemo(() => {
    const raw = (trial as any)?.standort_id ?? (trial as any)?.standortId ?? null;
    const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
  }, [trial]);

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const groups: RecipientGroup[] = [];
    const teacherWpUserId = Number(trial.teacherId || 0);
    const { wpUserId: studentWpUserId, subIndex: studentSubIndex } = parseSubprofileRecipient(String(trial.subprofileId || ''));
    const vars = {
      teacherName: trial.teacherName || '',
      studentName: trial.studentName || '',
      date: trial.date,
      startTime: trial.startTime,
      endTime: trial.endTime,
      roomName: trial.roomName || '',
      roomClause: trial.roomName ? ` im Raum ${trial.roomName}` : '',
      reason: reasonLabel,
    };

    if (teacherWpUserId > 0) {
      groups.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients: [{
          id: `person:${teacherWpUserId}:`,
          type: 'person',
          wpUserId: teacherWpUserId,
          subIndex: null,
          name: trial.teacherName || `Lehrer #${teacherWpUserId}`,
        }],
        templateKeys: { email: 'trial.cancelled.teacher' },
        vars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    if (studentWpUserId > 0) {
      groups.push({
        key: 'student',
        label: 'Schüler',
        recipients: [{
          id: `person:${studentWpUserId}:${studentSubIndex ?? ''}`,
          type: 'person',
          wpUserId: studentWpUserId,
          subIndex: studentSubIndex,
          name: trial.studentName || 'Schüler',
        }],
        templateKeys: { email: 'trial.cancelled.student' },
        vars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    return groups;
  }, [trial, reasonLabel]);

  const handleConfirm = async () => {
    const payload: any = {
      reasonKey,
      reasonText: reasonKey === 'other' ? (reasonText || undefined) : undefined,
      historyTemplateKey: 'trial.cancelled.history',
      history: {
        type: 'trial.cancelled',
        description: historyHtml,
        details: JSON.stringify({ reasonKey, reasonText: reasonKey === 'other' ? (reasonText || undefined) : undefined }),
      },
      task: {
        description: taskDescription,
        dueAt: dueAt.replace('T', ' ') + ':00',
        assignedUserId: assignedUserId as number,
      },
    };
    payload.drafts = {
      student: { subject: emailSubjectStudent, html: emailHtmlStudent, text: emailTextStudent },
      teacher: { subject: emailSubjectTeacher, html: emailHtmlTeacher, text: emailTextTeacher },
    };
    payload.channels = { email: sendEmailStudent || sendEmailTeacher, sms: false };
    payload.notify = {
      student: { email: sendEmailStudent, sms: false },
      teacher: { email: sendEmailTeacher, sms: false },
    };
    // Die Absage verschickt das Backend (Abnahme-Katalog PS-5.3); vorher tat
    // es dieser Assistent selbst im Browser.
    const res = await cancelTrialAdvanced(trial.id, payload);
    const failed = [res.communication?.emailError, res.communication?.smsError].filter(Boolean);
    if (failed.length > 0) {
      toast.warning(`Absage gespeichert, Versand fehlgeschlagen: ${failed.join(' ')}`);
    }
    onDone?.(res);
    onOpenChange(false);
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Probestunde absagen"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[
        { label: 'Grund' },
        { label: 'Kommunikation' },
        { label: 'Übersicht' },
      ]}
      onBack={step > 1 ? () => setStep((s) => Math.max(1, s - 1)) : undefined}
      canBack={step > 1}
      onNext={step < 3 ? () => setStep((s) => Math.min(3, s + 1)) : undefined}
      canNext={canMoveNext}
      nextLabel="Weiter"
      onPrimary={step === 3 ? handleConfirm : undefined}
      primaryLabel="Eintragen"
      canPrimary={step === 3 ? !!canConfirm : false}
    >
      <div className="py-4">
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Grund</Label>
              <WizardPills className="gap-1.5">
                <button type="button" onClick={() => setReasonKey('student_cancelled')}>
                  <WizardPill active={reasonKey === 'student_cancelled'}>Schüler hat abgesagt</WizardPill>
                </button>
                <button type="button" onClick={() => setReasonKey('teacher_cancelled')}>
                  <WizardPill active={reasonKey === 'teacher_cancelled'}>Lehrer hat abgesagt</WizardPill>
                </button>
                <button type="button" onClick={() => setReasonKey('no_show')}>
                  <WizardPill active={reasonKey === 'no_show'}>Schüler ist nicht erschienen</WizardPill>
                </button>
                <button type="button" onClick={() => setReasonKey('other')}>
                  <WizardPill active={reasonKey === 'other'}>Anderer Grund</WizardPill>
                </button>
              </WizardPills>
            </div>
            {reasonKey === 'other' && (
              <div className="space-y-2">
                <Label>Details zum Grund</Label>
                <Input value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="z. B. Krankheit, Streik, ..." />
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {composerGroups.length > 0 ? (
              <CommunicationComposer
                active={open && step === 2}
                standortId={standortId}
                groups={composerGroups}
                onDraftsChange={({ groups: g }) => {
                  const t = g['teacher'];
                  if (t) {
                    setSendEmailTeacher(!!t.selectedChannels.email);
                    setEmailSubjectTeacher(t.email.subjectTpl || '');
                    setEmailHtmlTeacher(t.email.htmlTpl || '');
                    setEmailTextTeacher(t.email.textTpl || '');
                  }
                  const s = g['student'];
                  if (s) {
                    setSendEmailStudent(!!s.selectedChannels.email);
                    setEmailSubjectStudent(s.email.subjectTpl || '');
                    setEmailHtmlStudent(s.email.htmlTpl || '');
                    setEmailTextStudent(s.email.textTpl || '');
                  }
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Keine Empfänger für den Versand gefunden.</p>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <WizardCard>
              <WizardSection title="Aufgabe & Historie">
                <div className="space-y-4">
                  <FaelligkeitFeld
                    id="absageAufgabe"
                    value={faelligkeitAusWert(dueAt)}
                    onChange={(f) => setDueAt(`${f.datum}T${f.uhrzeit ?? TAGESENDE}`)}
                  />
                  <ZuweisungFeld
                    id="absageAufgabeZuweisung"
                    value={assignedUserId}
                    users={users}
                    onChange={setAssignedUserId}
                  />
                  <div className="space-y-2">
                    <Label>Historieneintrag</Label>
                    <RichTextEditor
                      value={historyHtml}
                      onChange={(value) => {
                        setHistoryTouched(true);
                        setHistoryHtml(value);
                      }}
                    />
                    <p className="text-xs text-muted-foreground">Der Historientext kann hier vor dem Eintragen angepasst werden.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Aufgabenbeschreibung</Label>
                    <Textarea className="min-h-[120px]" value={taskDescription} onChange={e => { setTaskDescription(e.target.value); setTaskDescriptionTouched(true); }} />
                  </div>
                </div>
              </WizardSection>
              <WizardSection title="Zusammenfassung">
                <WizardKVGrid>
                  <WizardKVItem label="Schüler">{trial.studentName}</WizardKVItem>
                  <WizardKVItem label="Lehrer">{trial.teacherName || '-'}</WizardKVItem>
                  <WizardKVItem label="Termin">{`${formatDate(trial.date)} ${trial.startTime.slice(0, 5)}-${trial.endTime.slice(0, 5)}`}</WizardKVItem>
                  <WizardKVItem label="Grund">{reasonLabel}</WizardKVItem>
                  <WizardKVItem label="Aufgabe fällig">{faelligkeitText(faelligkeitAusWert(dueAt))}</WizardKVItem>
                  <WizardKVItem label="Zugewiesen an">{users.find(u => u.id === assignedUserId)?.name || `User #${assignedUserId}`}</WizardKVItem>
                </WizardKVGrid>
              </WizardSection>
              <WizardSection title="Benachrichtigungen">
                <WizardKVGrid>
                  <WizardKVItem label="E-Mail an Lehrer"><WizardYesNoBadge yes={sendEmailTeacher} /></WizardKVItem>
                  <WizardKVItem label="E-Mail an Schüler"><WizardYesNoBadge yes={sendEmailStudent} /></WizardKVItem>
                </WizardKVGrid>
              </WizardSection>
            </WizardCard>
          </div>
        )}
      </div>
    </WizardShell>
  );
};

export default CancelTrialWizardModal;
